// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import useBuildAjsFileNoAudit from 'ion-experimental/api/ajs/useBuildAjsFileNoAudit';
import buildFileOptions from '@ion/app/src/components/analytics-builder/buildFileOptions';
import buildSettingsTemplateFromFile from '../shared/buildSettingsTemplateFromFile';
import processInBatches from '../shared/processInBatches';
import BULK_OPERATION_STATUS from '../shared/BULK_OPERATION_STATUS';

const MODAL_STEP = {
  SELECT: 'SELECT',
  CONFIRM: 'CONFIRM',
};

export default function useBulkBuildAndDownload({ analyticsFiles, consentCategories, hasConsentModule, onComplete }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [status, setStatus] = useState(BULK_OPERATION_STATUS.IDLE);
  const [step, setStep] = useState(MODAL_STEP.SELECT);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState({ successful: [], failed: [] });

  const [buildAjsFile] = useBuildAjsFileNoAudit();

  const selectedFiles = analyticsFiles?.filter(file => selectedIds.has(file.id)) ?? [];

  const selectAll = useCallback(() => {
    const allIds = new Set(analyticsFiles?.map(file => file.id) ?? []);
    setSelectedIds(allIds);
  }, [analyticsFiles]);

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const toggleSelection = useCallback(fileId => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const goToConfirm = useCallback(() => {
    setStep(MODAL_STEP.CONFIRM);
  }, []);

  const goToSelect = useCallback(() => {
    setStep(MODAL_STEP.SELECT);
  }, []);

  const buildAndDownload = useCallback(async () => {
    setStatus(BULK_OPERATION_STATUS.PROCESSING);
    setProgress({ current: 0, total: selectedFiles.length });

    const buildFile = async file => {
      const pipelineName = file.pipeline?.name ?? 'Unknown Pipeline';
      const settingsTemplate = buildSettingsTemplateFromFile({ file, consentCategories, hasConsentModule });
      const { settingsJson } = buildFileOptions(settingsTemplate, false);
      const result = await buildAjsFile({ settingsJson });

      if (result.errors) {
        const errorMessage = result.errors[0]?.message ?? 'Unknown error';
        throw new Error(`${pipelineName}: ${errorMessage}`);
      }

      const buildResult = result.data?.build_ajs_file;
      if (buildResult?.status !== 'success' && buildResult?.status !== 200) {
        const errorDetail = buildResult?.data ?? `status ${buildResult?.status ?? 'unknown'}`;
        throw new Error(`${pipelineName}: ${errorDetail}`);
      }

      return {
        file,
        content: buildResult.data,
        filename: `${file.pipeline?.writekey ?? file.id}.js`,
      };
    };

    const buildResults = await processInBatches({
      items: selectedFiles,
      processItem: buildFile,
      onProgress: (current, total) => setProgress({ current, total }),
    });

    setResults(buildResults);
    setStatus(BULK_OPERATION_STATUS.COMPLETE);

    if (buildResults.successful.length > 0) {
      await downloadAsZip(buildResults.successful);
    }

    onComplete?.();
  }, [selectedFiles, buildAjsFile, consentCategories, hasConsentModule, onComplete]);

  const reset = useCallback(() => {
    setSelectedIds(new Set());
    setStatus(BULK_OPERATION_STATUS.IDLE);
    setStep(MODAL_STEP.SELECT);
    setProgress({ current: 0, total: 0 });
    setResults({ successful: [], failed: [] });
  }, []);

  return {
    selectedIds,
    setSelectedIds,
    selectedFiles,
    status,
    step,
    progress,
    results,
    selectAll,
    deselectAll,
    toggleSelection,
    goToConfirm,
    goToSelect,
    buildAndDownload,
    reset,
    MODAL_STEP,
  };
}

async function downloadAsZip(builtFiles) {
  const zip = new JSZip();

  for (const { filename, content } of builtFiles) {
    zip.file(filename, content);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  link.download = `analytics-files-${timestamp}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
